import { useEffect, useRef } from 'react';

import pluralize from 'pluralize';

import { CAST_AI } from '@cast/constants';
import {
  Button,
  DialogV2,
  Inset,
  makeDialog,
  notify,
  useCloseDialog,
  ExternalLink,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';
import { useMarkSalesContactedMutation } from '@cast/shared-trial-data-access';
import { useTrial, PremiumFeaturesList } from '@cast/shared-trial-ui';

import { openScheduleDialog } from 'core/integrations';

import { getDocsLink } from '@cast/utils';

type TrialHasEndedDialogContentProps = {
  onClose?: () => void;
};

const TrialHasEndedDialogContent = ({
  onClose,
}: TrialHasEndedDialogContentProps) => {
  const closeDialog = useCloseDialog();
  const { state, expiresInDaysGracePeriod } = useTrial();
  const { mutateAsync: markSalesContacted } = useMarkSalesContactedMutation();

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    return () => {
      onCloseRef.current?.();
    };
  }, []);

  const handleScheduleCall = () => {
    openScheduleDialog({
      onMeetingBooked: () => {
        markSalesContacted()
          .catch(() => {
            notify.error(
              'Failed to save your contact status. Please try again later.'
            );
          })
          .finally(() => {
            closeDialog();
          });
      },
      onError: () =>
        notify.error(
          'Failed to load the booking form. Please try again later.'
        ),
    });
  };

  return (
    <DialogV2
      testId="trial-has-ended-dialog"
      title={`Choose how to continue with ${CAST_AI}`}
      footerDivider
      endActions={
        <Button
          variant="primary"
          testId="schedule-sales-call-button"
          onClick={handleScheduleCall}
        >
          Talk to us to upgrade
        </Button>
      }
    >
      <Inset pb={24}>
        <Stack gap={24}>
          <Typography variant="P12R" color={tokens.theme.fg.status.error}>
            Your trial{' '}
            {state === 'grace-period'
              ? 'has ended'
              : 'and grace period have ended'}
            . Please upgrade to continue.
          </Typography>

          <Stack gap={8}>
            <Typography variant="h6" color={tokens.theme.fg.structural.primary}>
              Upgrade to premium
            </Typography>
            <PremiumFeaturesList />
          </Stack>

          <Typography
            variant="P12R"
            color={tokens.theme.fg.structural.secondary}
          >
            Premium features{' '}
            {state === 'grace-period' ? (
              <>
                will become unavailable in{' '}
                <strong>
                  {expiresInDaysGracePeriod}{' '}
                  {pluralize('day', expiresInDaysGracePeriod)}
                </strong>
              </>
            ) : (
              <strong>are unavailable</strong>
            )}
            . Schedule a call with our team to discuss your needs and continue
            with full platform capabilities.
          </Typography>

          <Stack gap={4}>
            <Typography variant="h6" color={tokens.theme.fg.structural.primary}>
              What will happen to my clusters after the trial if I do nothing?
            </Typography>

            <Typography
              variant="P12R"
              color={tokens.theme.fg.structural.secondary}
            >
              Your clusters and workloads will remain fully operational. If you
              downgrade or take no action, you&apos;ll retain access to free
              cost monitoring features while premium capabilities are disabled.
              You can choose to upgrade again at any time. For more details, see
              our{' '}
              <ExternalLink link={getDocsLink('docs_trial')} noIcon>
                documentation
              </ExternalLink>
              .
            </Typography>
          </Stack>
        </Stack>
      </Inset>
    </DialogV2>
  );
};

export const useTrialHasEndedDialog = makeDialog(TrialHasEndedDialogContent);
